import {
    AnyTask,
    AssignmentType,
    AssortmentTaskType,
    Coords,
    DeliveryPromo,
    Market,
    TaskRoute,
    TaskStatus,
} from "tm-shared/types";

export enum ModalType {
    StartVisit = "startVisit",
    StartVisitWarning = "startVisitWarning",
    Comment = "comment",
    TaskInfo = "taskInfo",
    Promos = "promos",
    UncompletedCheckouts = "uncompleted-checkouts",
    UncompletedTasks = "uncompleted-tasks",
    KpiProgress = "kpi-progress",
    Messages = "messages",
    Exports = "exports",
    State = "state",
    UnscheduledMarketCompletion = "uncheduled-market-completion",
    CreateCustomTask = "createCustomTask",
    CustomTaskAddPhotos = "customTaskAddPhotos",
}

export type ModalPayloads = {
    [ModalType.StartVisit]: { taskId: string; market: Market };
    [ModalType.StartVisitWarning]: {
        taskId: string;
        market: Market;
        dist: number;
        coords: Coords;
    };
    [ModalType.Comment]: {
        initialComment: string;
        taskStatus?: TaskStatus.Completed | TaskStatus.Rejected;
    } & (
        | {
              route: typeof TaskRoute.Dmp;
              taskId: string;
              contractId: string;
              dmpId: string;
          }
        | {
              route: typeof TaskRoute.PromoTagPhotos;
              taskId: string;
              barcode: string;
          }
        | {
              route: typeof AssortmentTaskType.FinGroupPhotos;
              taskId: string;
              contractId: string;
              subtaskId: string;
              id: string;
          }
        | {
              route: typeof AssortmentTaskType.ProductsMatrix;
              taskId: string;
              subtaskId: string;
              contractId: string;
              barcode: string;
          }
        | {
              route: typeof AssignmentType.Inventory;
              taskId: string;
              barcode: string;
          }
    );

    [ModalType.TaskInfo]: {
        title: string;
        description: string;
        files?: string[];
    };

    [ModalType.Promos]: {
        deliveryId: string;
        promoInfo: DeliveryPromo;
    };

    [ModalType.Messages]: {
        subject: string;
        content: string;
        deliveryId: string;
    };

    [ModalType.KpiProgress]: {
        deliveryId: string;
        calculatedAt: string;
    };

    [ModalType.Exports]: {
        description: string;
        path: string;
        filename: string;
        deliveryId: string;
    };

    [ModalType.State]: { deliveryId: string };

    [ModalType.UnscheduledMarketCompletion]: {
        deliveryId: string;
        marketsCount: number;
    };

    [ModalType.CustomTaskAddPhotos]: {
        market: Market;
        task: AnyTask;
        images: string[];
    };
    [ModalType.CreateCustomTask]: {
        market?: Market;
        agentId?: string;
        images: string[];
    };
};

export type ModalState = { id: string } & (
    | {
          type: ModalType.Promos;
          payload: ModalPayloads[ModalType.Promos];
      }
    | {
          type: ModalType.UncompletedCheckouts;
      }
    | {
          type: ModalType.UncompletedTasks;
      }
    | {
          type: ModalType.KpiProgress;
          payload: ModalPayloads[ModalType.KpiProgress];
      }
    | {
          type: ModalType.Messages;
          payload: ModalPayloads[ModalType.Messages];
      }
    | {
          type: ModalType.Exports;
          payload: ModalPayloads[ModalType.Exports];
      }
    | {
          type: ModalType.State;
          payload: ModalPayloads[ModalType.State];
      }
    | {
          type: ModalType.StartVisit;
          payload: ModalPayloads[ModalType.StartVisit];
      }
    | {
          type: ModalType.StartVisitWarning;
          payload: ModalPayloads[ModalType.StartVisitWarning];
      }
    | {
          type: ModalType.Comment;
          payload: ModalPayloads[ModalType.Comment];
      }
    | {
          type: ModalType.TaskInfo;
          payload: ModalPayloads[ModalType.TaskInfo];
      }
    | {
          type: ModalType.UnscheduledMarketCompletion;
          payload: ModalPayloads[ModalType.UnscheduledMarketCompletion];
      }
    | {
          type: ModalType.CustomTaskAddPhotos;
          payload: ModalPayloads[ModalType.CustomTaskAddPhotos];
      }
    | {
          type: ModalType.CreateCustomTask;
          payload: ModalPayloads[ModalType.CreateCustomTask];
      }
    | null
);

// ---------------------- Market Create Page ---------------------

export enum MarketCreateModal {
    AddAddress = "addAddress",
    AddLegalEntity = "addLegalEntity",
    AddIllegalEntity = "addIllegalEntity",
    DuplicateByAddress = "duplicateByAddress",
    DuplicateById = "duplicateById",
    AddNetwork = "addNetwork",
}

export interface MarketCreateModalPayloads {
    [MarketCreateModal.DuplicateById]: {
        market: Market;
        routeCount: number;
    };
}

export type MarketCreateModalState =
    | {
          type: MarketCreateModal.AddAddress;
          payload: AddAddressModalPayload;
      }
    | {
          type: MarketCreateModal.AddLegalEntity;
      }
    | {
          type: MarketCreateModal.DuplicateByAddress;
          payload: DuplicateByAddressModalPayload;
      }
    | {
          type: MarketCreateModal.DuplicateById;
          payload: MarketCreateModalPayloads[MarketCreateModal.DuplicateById];
      }
    | {
          type: MarketCreateModal.AddNetwork;
      }
    | {
          type: MarketCreateModal.AddIllegalEntity;
      };

//  ------------------------- Shared Modals --------------------

export interface AddAddressModalPayload {
    region: string;
    regionId: string;
    variant?: "createMarket" | "verifyAddress";
}

export interface DuplicateByAddressModalPayload {
    markets: Market[];
    address: string;
    variant?: "createMarket" | "verifyAddress";
}
